// src/features/sheets/utils/liveDataService.ts
// Service for fetching live crypto data in spreadsheet LIVE() formulas

import { fetchTokenCurrentValues, type MetricSpec } from "@/services/data/fetchers/tokenFetchers";


// ─────────────────────────────────────────────────────────────────────────────
// Metric Key Mapping
// ─────────────────────────────────────────────────────────────────────────────

/** Maps user-friendly aliases to API MetricSpec */
const METRIC_ALIASES: Map<string, MetricSpec> = new Map([
  // ─────────────────────────────────────────────────────────────────────────────
  // Price & Market Data
  // ─────────────────────────────────────────────────────────────────────────────
  ["price", { key: "price_close" }],
  ["price_close", { key: "price_close" }],
  ["mcap", { key: "market_cap" }],
  ["market_cap", { key: "market_cap" }],
  ["marketcap", { key: "market_cap" }],
  ["fdv", { key: "fdv" }],
  ["volume", { key: "volume" }],
  ["vol", { key: "volume" }],

  // ─────────────────────────────────────────────────────────────────────────────
  // Volatility & Risk
  // ─────────────────────────────────────────────────────────────────────────────
  ["vol_30d", { key: "vol_30d" }],
  ["volatility_30d", { key: "vol_30d" }],
  ["vol_90d", { key: "vol_90d" }],
  ["volatility_90d", { key: "vol_90d" }],
  ["vol_180d", { key: "vol_180d" }],
  ["volatility_180d", { key: "vol_180d" }],
  ["vol_365d", { key: "vol_365d" }],
  ["volatility_365d", { key: "vol_365d" }],
  ["max_drawdown_90d", { key: "max_drawdown_90d" }],
  ["drawdown_90d", { key: "max_drawdown_90d" }],
  ["max_drawdown_365d", { key: "max_drawdown_365d" }],
  ["drawdown_365d", { key: "max_drawdown_365d" }],

  // ─────────────────────────────────────────────────────────────────────────────
  // Risk-Adjusted Returns
  // ─────────────────────────────────────────────────────────────────────────────
  ["sharpe_30d", { key: "sharpe_30d" }],
  ["sharpe_90d", { key: "sharpe_90d" }],
  ["sharpe_180d", { key: "sharpe_180d" }],
  ["sharpe_365d", { key: "sharpe_365d" }],
  ["sharpe_1y", { key: "sharpe_365d" }],
  ["sortino_30d", { key: "sortino_30d" }],
  ["sortino_90d", { key: "sortino_90d" }],
  ["sortino_180d", { key: "sortino_180d" }],
  ["sortino_365d", { key: "sortino_365d" }],
  ["sortino_1y", { key: "sortino_365d" }],

  // ─────────────────────────────────────────────────────────────────────────────
  // On-Chain Activity
  // ─────────────────────────────────────────────────────────────────────────────
  ["tvl", { key: "tvl" }],
  ["dau", { key: "dau" }],
  ["daily_active_users", { key: "dau" }],
  ["active_users", { key: "dau" }],
  ["tx_count", { key: "tx_count" }],
  ["transactions", { key: "tx_count" }],
  ["txns", { key: "tx_count" }],
  ["dex_volume", { key: "dex_volume" }],
  ["dex_vol", { key: "dex_volume" }],

  // ─────────────────────────────────────────────────────────────────────────────
  // Protocol Revenue
  // ─────────────────────────────────────────────────────────────────────────────
  ["fees", { key: "fees" }],
  ["revenue", { key: "revenue" }],
  ["rev", { key: "revenue" }],

  // ─────────────────────────────────────────────────────────────────────────────
  // Derivatives / Futures
  // ─────────────────────────────────────────────────────────────────────────────
  ["futures_oi", { key: "futures_oi" }],
  ["open_interest", { key: "futures_oi" }],
  ["oi", { key: "futures_oi" }],
  ["futures_fr", { key: "futures_fr" }],
  ["funding_rate", { key: "futures_fr" }],
  ["futures_fr_oiw", { key: "futures_fr_oiw" }],
  ["funding_rate_oiw", { key: "futures_fr_oiw" }],
  ["futures_fr_volw", { key: "futures_fr_volw" }],
  ["funding_rate_volw", { key: "futures_fr_volw" }],

  // ─────────────────────────────────────────────────────────────────────────────
  // Valuation Ratios (canonical and aliases)
  // ─────────────────────────────────────────────────────────────────────────────
  // MC/TVL
  ["ratio:marketcaptotvl", { key: "ratio:marketCapToTvl" }],
  ["mc_to_tvl", { key: "ratio:marketCapToTvl" }],
  ["mcap_to_tvl", { key: "ratio:marketCapToTvl" }],
  ["mcap/tvl", { key: "ratio:marketCapToTvl" }],
  // MC/Fees
  ["ratio:marketcaptoannualisedfees", { key: "ratio:marketCapToAnnualisedFees" }],
  ["mc_to_fees", { key: "ratio:marketCapToAnnualisedFees" }],
  ["mcap_to_fees", { key: "ratio:marketCapToAnnualisedFees" }],
  ["mcap/fees", { key: "ratio:marketCapToAnnualisedFees" }],
  // FDV/Fees
  ["ratio:fdvtofees", { key: "ratio:fdvToFees" }],
  ["fdv_to_fees", { key: "ratio:fdvToFees" }],
  ["fdv/fees", { key: "ratio:fdvToFees" }],
  // MC/DAU
  ["ratio:marketcaptodau", { key: "ratio:marketCapToDau" }],
  ["mc_to_dau", { key: "ratio:marketCapToDau" }],
  ["mcap_to_dau", { key: "ratio:marketCapToDau" }],
  // MC/DEX Volume
  ["ratio:marketcaptodextradingvolume", { key: "ratio:marketCapToDexTradingVolume" }],
  ["mc_to_dex_volume", { key: "ratio:marketCapToDexTradingVolume" }],
  ["mcap_to_dex_volume", { key: "ratio:marketCapToDexTradingVolume" }],
  // MC/Tx Count
  ["ratio:marketcaptotxcount", { key: "ratio:marketCapToTxCount" }],
  ["mc_to_tx_count", { key: "ratio:marketCapToTxCount" }],
  ["mcap_to_txcount", { key: "ratio:marketCapToTxCount" }],
  // TVL/Fees
  ["ratio:tvltoannualisedfees", { key: "ratio:tvlToAnnualisedFees" }],
  ["tvl_to_fees", { key: "ratio:tvlToAnnualisedFees" }],
  // TVL/DEX Volume
  ["ratio:tvltodextradingvolume", { key: "ratio:tvlToDexTradingVolume" }],
  ["tvl_to_dex_volume", { key: "ratio:tvlToDexTradingVolume" }],
  // Fees/Volume
  ["ratio:feestovolume", { key: "ratio:feesToVolume" }],
  ["fees_to_volume", { key: "ratio:feesToVolume" }],
  // OI/TVL
  ["ratio:oitotvl", { key: "ratio:oiToTvl" }],
  ["oi_to_tvl", { key: "ratio:oiToTvl" }],
  // OI/Volume
  ["ratio:oitovolume", { key: "ratio:oiToVolume" }],
  ["oi_to_volume", { key: "ratio:oiToVolume" }],
  // Tx per User
  ["ratio:txperuser", { key: "ratio:txPerUser" }],
  ["tx_per_user", { key: "ratio:txPerUser" }],
  ["txns_per_user", { key: "ratio:txPerUser" }],
  // Fees per DAU
  ["ratio:annualisedfeetoactiveaddresses", { key: "ratio:annualisedFeesToActiveAdresses" }],
  ["fees_per_dau", { key: "ratio:annualisedFeesToActiveAdresses" }],
  // Fees per Tx
  ["ratio:annualisedfeestotransactions", { key: "ratio:annualisedFeesToTransactions" }],
  ["fees_per_tx", { key: "ratio:annualisedFeesToTransactions" }],

  // ─────────────────────────────────────────────────────────────────────────────
  // Price Changes (computed with transform)
  // ─────────────────────────────────────────────────────────────────────────────
  ["change_1d", { key: "price_close", transform: "change", window: "1d" }],
  ["1d_change", { key: "price_close", transform: "change", window: "1d" }],
  ["change_24h", { key: "price_close", transform: "change", window: "1d" }],
  ["change_7d", { key: "price_close", transform: "change", window: "7d" }],
  ["7d_change", { key: "price_close", transform: "change", window: "7d" }],
  ["change_30d", { key: "price_close", transform: "change", window: "30d" }],
  ["30d_change", { key: "price_close", transform: "change", window: "30d" }],
]);

/**
 * Maps user-friendly metric keys to API keys
 */
function normalizeMetricKey(key: string): MetricSpec {
  const normalized = key.toLowerCase().trim();
  return METRIC_ALIASES.get(normalized) ?? { key: normalized };
}

/**
 * Normalizes ticker symbols
 */
function normalizeTicker(ticker: string): string {
  return ticker.toUpperCase().trim();
}

// ─────────────────────────────────────────────────────────────────────────────
// Cache
// ─────────────────────────────────────────────────────────────────────────────

interface CacheEntry {
  value: number;
  timestamp: number;
}

// Simple in-memory cache with TTL
const cache = new Map<string, CacheEntry>();
const CACHE_TTL = 60 * 1000; // 1 minute cache

function getCacheKey(ticker: string, metric: string): string {
  return `${ticker}:${metric}`;
}

function getCachedValue(ticker: string, metric: string): number | null {
  const key = getCacheKey(ticker, metric);
  const entry = cache.get(key);
  if (entry && Date.now() - entry.timestamp < CACHE_TTL) {
    return entry.value;
  }
  return null;
}

function setCachedValue(ticker: string, metric: string, value: number): void {
  const key = getCacheKey(ticker, metric);
  cache.set(key, { value, timestamp: Date.now() });
}

// ─────────────────────────────────────────────────────────────────────────────
// Pending Requests (for deduplication)
// ─────────────────────────────────────────────────────────────────────────────

const pendingRequests = new Map<string, Promise<number | null>>();

// ─────────────────────────────────────────────────────────────────────────────
// Main Fetch Function
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetches a single live metric value for a ticker.
 * Handles caching and request deduplication.
 */
export async function fetchLiveValue(
  ticker: string,
  metricKey: string
): Promise<number | null> {
  const normalizedTicker = normalizeTicker(ticker);
  const metricSpec = normalizeMetricKey(metricKey);
  const cacheKey = getCacheKey(normalizedTicker, JSON.stringify(metricSpec));

  // Check cache first
  const cached = getCachedValue(normalizedTicker, JSON.stringify(metricSpec));
  if (cached !== null) {
    return cached;
  }

  // Check if there's already a pending request
  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey)!;
  }

  // Create new request
  const request = (async () => {
    try {
      const result = await fetchTokenCurrentValues([normalizedTicker], metricSpec);
      const value = result[normalizedTicker];

      if (value !== undefined && value !== null) {
        setCachedValue(normalizedTicker, JSON.stringify(metricSpec), value);
        return value;
      }

      return null;
    } catch (error) {
      console.error(`Error fetching LIVE data for ${normalizedTicker}/${metricKey}:`, error);
      return null;
    } finally {
      pendingRequests.delete(cacheKey);
    }
  })();

  pendingRequests.set(cacheKey, request);
  return request;
}

// ─────────────────────────────────────────────────────────────────────────────
// Available Metrics Documentation
// ─────────────────────────────────────────────────────────────────────────────

export const AVAILABLE_METRICS = [
  // ─────────────────────────────────────────────────────────────────────────────
  // Price & Market Data
  // ─────────────────────────────────────────────────────────────────────────────
  { key: "price_close", description: "Current price", example: '=LIVE("BTC", "price_close")', category: "price" },
  { key: "market_cap", description: "Market capitalization", example: '=LIVE("ETH", "market_cap")', category: "price" },
  { key: "fdv", description: "Fully Diluted Valuation", example: '=LIVE("SOL", "fdv")', category: "price" },
  { key: "volume", description: "24h trading volume", example: '=LIVE("BTC", "volume")', category: "price" },

  // ─────────────────────────────────────────────────────────────────────────────
  // Volatility & Risk
  // ─────────────────────────────────────────────────────────────────────────────
  { key: "vol_30d", description: "Volatility (30d, annualized)", example: '=LIVE("BTC", "vol_30d")', category: "risk" },
  { key: "vol_90d", description: "Volatility (90d, annualized)", example: '=LIVE("BTC", "vol_90d")', category: "risk" },
  { key: "vol_180d", description: "Volatility (180d, annualized)", example: '=LIVE("BTC", "vol_180d")', category: "risk" },
  { key: "vol_365d", description: "Volatility (365d, annualized)", example: '=LIVE("BTC", "vol_365d")', category: "risk" },
  { key: "max_drawdown_90d", description: "Max Drawdown (90d)", example: '=LIVE("ETH", "max_drawdown_90d")', category: "risk" },
  { key: "max_drawdown_365d", description: "Max Drawdown (365d)", example: '=LIVE("ETH", "max_drawdown_365d")', category: "risk" },

  // ─────────────────────────────────────────────────────────────────────────────
  // Risk-Adjusted Returns
  // ─────────────────────────────────────────────────────────────────────────────
  { key: "sharpe_30d", description: "Sharpe Ratio (30d)", example: '=LIVE("BTC", "sharpe_30d")', category: "risk_adjusted" },
  { key: "sharpe_90d", description: "Sharpe Ratio (90d)", example: '=LIVE("BTC", "sharpe_90d")', category: "risk_adjusted" },
  { key: "sharpe_180d", description: "Sharpe Ratio (180d)", example: '=LIVE("BTC", "sharpe_180d")', category: "risk_adjusted" },
  { key: "sharpe_365d", description: "Sharpe Ratio (365d)", example: '=LIVE("BTC", "sharpe_365d")', category: "risk_adjusted" },
  { key: "sortino_30d", description: "Sortino Ratio (30d)", example: '=LIVE("ETH", "sortino_30d")', category: "risk_adjusted" },
  { key: "sortino_90d", description: "Sortino Ratio (90d)", example: '=LIVE("ETH", "sortino_90d")', category: "risk_adjusted" },
  { key: "sortino_180d", description: "Sortino Ratio (180d)", example: '=LIVE("ETH", "sortino_180d")', category: "risk_adjusted" },
  { key: "sortino_365d", description: "Sortino Ratio (365d)", example: '=LIVE("ETH", "sortino_365d")', category: "risk_adjusted" },

  // ─────────────────────────────────────────────────────────────────────────────
  // On-Chain Activity
  // ─────────────────────────────────────────────────────────────────────────────
  { key: "dau", description: "Daily Active Users", example: '=LIVE("SOL", "dau")', category: "onchain" },
  { key: "tx_count", description: "Daily Transactions", example: '=LIVE("SOL", "tx_count")', category: "onchain" },
  { key: "tvl", description: "Total Value Locked", example: '=LIVE("AAVE", "tvl")', category: "onchain" },
  { key: "dex_volume", description: "DEX Trading Volume", example: '=LIVE("UNI", "dex_volume")', category: "onchain" },

  // ─────────────────────────────────────────────────────────────────────────────
  // Protocol Revenue
  // ─────────────────────────────────────────────────────────────────────────────
  { key: "fees", description: "Protocol Fees", example: '=LIVE("UNI", "fees")', category: "revenue" },
  { key: "revenue", description: "Protocol Revenue", example: '=LIVE("MKR", "revenue")', category: "revenue" },

  // ─────────────────────────────────────────────────────────────────────────────
  // Derivatives / Futures
  // ─────────────────────────────────────────────────────────────────────────────
  { key: "futures_oi", description: "Open Interest", example: '=LIVE("BTC", "futures_oi")', category: "derivatives" },
  { key: "futures_fr", description: "Funding Rate", example: '=LIVE("BTC", "futures_fr")', category: "derivatives" },
  { key: "futures_fr_oiw", description: "Funding Rate (OI-Weighted)", example: '=LIVE("ETH", "futures_fr_oiw")', category: "derivatives" },
  { key: "futures_fr_volw", description: "Funding Rate (Vol-Weighted)", example: '=LIVE("ETH", "futures_fr_volw")', category: "derivatives" },

  // ─────────────────────────────────────────────────────────────────────────────
  // Valuation Ratios
  // ─────────────────────────────────────────────────────────────────────────────
  { key: "ratio:marketCapToTvl", description: "Market Cap / TVL", example: '=LIVE("AAVE", "ratio:marketCapToTvl")', category: "ratios" },
  { key: "ratio:marketCapToAnnualisedFees", description: "Market Cap / Annualized Fees", example: '=LIVE("UNI", "ratio:marketCapToAnnualisedFees")', category: "ratios" },
  { key: "ratio:fdvToFees", description: "FDV / Annualized Fees", example: '=LIVE("MKR", "ratio:fdvToFees")', category: "ratios" },
  { key: "ratio:marketCapToDau", description: "Market Cap / DAU", example: '=LIVE("SOL", "ratio:marketCapToDau")', category: "ratios" },
  { key: "ratio:marketCapToDexTradingVolume", description: "Market Cap / DEX Volume", example: '=LIVE("UNI", "ratio:marketCapToDexTradingVolume")', category: "ratios" },
  { key: "ratio:marketCapToTxCount", description: "Market Cap / Transactions", example: '=LIVE("SOL", "ratio:marketCapToTxCount")', category: "ratios" },
  { key: "ratio:tvlToAnnualisedFees", description: "TVL / Annualized Fees", example: '=LIVE("AAVE", "ratio:tvlToAnnualisedFees")', category: "ratios" },
  { key: "ratio:tvlToDexTradingVolume", description: "TVL / DEX Volume", example: '=LIVE("UNI", "ratio:tvlToDexTradingVolume")', category: "ratios" },
  { key: "ratio:feesToVolume", description: "Fees / Volume", example: '=LIVE("UNI", "ratio:feesToVolume")', category: "ratios" },
  { key: "ratio:oiToTvl", description: "Futures OI / TVL", example: '=LIVE("ETH", "ratio:oiToTvl")', category: "ratios" },
  { key: "ratio:oiToVolume", description: "Futures OI / Volume", example: '=LIVE("BTC", "ratio:oiToVolume")', category: "ratios" },
  { key: "ratio:txPerUser", description: "Transactions per User", example: '=LIVE("SOL", "ratio:txPerUser")', category: "ratios" },
  { key: "ratio:annualisedFeesToActiveAdresses", description: "Annualized Fees / DAU", example: '=LIVE("AAVE", "ratio:annualisedFeesToActiveAdresses")', category: "ratios" },
  { key: "ratio:annualisedFeesToTransactions", description: "Annualized Fees / Tx Count", example: '=LIVE("SOL", "ratio:annualisedFeesToTransactions")', category: "ratios" },

  // ─────────────────────────────────────────────────────────────────────────────
  // Price Changes (legacy aliases still supported)
  // ─────────────────────────────────────────────────────────────────────────────
  { key: "change_1d", description: "24h price change %", example: '=LIVE("BTC", "change_1d")', category: "changes" },
  { key: "change_7d", description: "7d price change %", example: '=LIVE("ETH", "change_7d")', category: "changes" },
  { key: "change_30d", description: "30d price change %", example: '=LIVE("SOL", "change_30d")', category: "changes" },
];
